import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useApiClient } from '../services/apiClient';
import { AVDSession } from '../types/session';
import SessionCard from './SessionCard';
import ConfirmDialog from './ConfirmDialog';

const AUTO_REFRESH_INTERVAL_MS = 60_000;

interface SessionListProps {
  onAdminChange?: (isAdmin: boolean) => void;
}

function formatDate(value?: string): string {
  if (!value) {
    return '-';
  }

  return new Date(value).toLocaleString('de-AT');
}

export default function SessionList({ onAdminChange }: SessionListProps) {
  const [sessions, setSessions] = useState<AVDSession[]>([]);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [userName, setUserName] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [terminatingSession, setTerminatingSession] = useState<string | null>(null);
  const [sessionToTerminate, setSessionToTerminate] = useState<AVDSession | null>(null);
  const [isAdminUser, setIsAdminUser] = useState(false);
  const [search, setSearch] = useState('');
  const [hostPoolFilter, setHostPoolFilter] = useState('all');

  const apiClient = useApiClient();
  const apiClientRef = useRef(apiClient);
  apiClientRef.current = apiClient;

  const applyResponse = useCallback(
    (response: Awaited<ReturnType<typeof apiClient.getSessions>>) => {
      setSessions(response.sessions);
      setWarnings(response.warnings || []);
      setUserName(response.user.name || response.user.upn);
      setIsAdminUser(response.permissions.isAdmin);
      onAdminChange?.(response.permissions.isAdmin);
    },
    [onAdminChange] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const loadSessions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      applyResponse(await apiClientRef.current.getSessions());
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Sitzungen konnten nicht geladen werden.';
      setError(message);
      console.error('Error loading sessions:', err);
    } finally {
      setLoading(false);
    }
  }, [applyResponse]);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  useEffect(() => {
    const interval = setInterval(() => {
      apiClientRef.current
        .getSessions()
        .then((response) => {
          applyResponse(response);
          setError(null);
        })
        .catch(() => undefined);
    }, AUTO_REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [applyResponse]);

  const hostPools = useMemo(
    () => Array.from(new Set(sessions.map((session) => session.hostPoolName))).sort(),
    [sessions]
  );

  const filteredSessions = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    return sessions.filter((session) => {
      const matchesHostPool =
        hostPoolFilter === 'all' || session.hostPoolName === hostPoolFilter;
      const matchesSearch =
        !normalizedSearch ||
        [session.userPrincipalName, session.hostPoolName, session.sessionHostName, session.sessionState]
          .join(' ')
          .toLowerCase()
          .includes(normalizedSearch);

      return matchesHostPool && matchesSearch;
    });
  }, [sessions, search, hostPoolFilter]);

  const handleConfirmTerminate = async () => {
    if (!sessionToTerminate) return;

    try {
      setTerminatingSession(sessionToTerminate.id);
      await apiClient.terminateSession(sessionToTerminate.id);
      setSessions((prev) => prev.filter((session) => session.id !== sessionToTerminate.id));
      setSessionToTerminate(null);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Sitzung konnte nicht beendet werden.';
      setError(message);
      console.error('Error terminating session:', err);
    } finally {
      setTerminatingSession(null);
    }
  };

  if (loading) {
    return (
      <div className="panel flex min-h-[320px] items-center justify-center">
        <div className="text-center">
          <div className="mx-auto mb-4 h-9 w-9 animate-spin rounded-full border-2 border-line border-b-red"></div>
          <p className="text-muted">Sitzungen werden geladen...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="notice-error mx-auto max-w-2xl">
        <p className="font-medium">Sitzungen konnten nicht geladen werden</p>
        <p className="mt-1 text-sm">{error}</p>
        <button onClick={loadSessions} className="btn-primary mt-4" type="button">
          Erneut laden
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="eyebrow">{isAdminUser ? 'Adminansicht' : 'Meine Sitzungen'}</p>
          <h2 className="mt-2 text-3xl font-semibold text-ink">Aktive AVD-Sitzungen</h2>
          <p className="mt-2 text-sm text-muted">{userName}</p>
        </div>
        <button onClick={loadSessions} className="btn-secondary" type="button">
          Aktualisieren
        </button>
      </div>

      {warnings.length > 0 && (
        <div className="notice-warning">
          {warnings.map((warning) => (
            <p key={warning}>{warning}</p>
          ))}
        </div>
      )}

      {isAdminUser && (
        <div className="panel grid gap-4 p-5 md:grid-cols-[1fr_260px]">
          <label>
            <span className="field-label">Suche</span>
            <input
              className="field-input"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Benutzer, Host Pool, Session Host"
            />
          </label>
          <label>
            <span className="field-label">Host Pool</span>
            <select
              className="field-input"
              value={hostPoolFilter}
              onChange={(event) => setHostPoolFilter(event.target.value)}
            >
              <option value="all">Alle Host Pools</option>
              {hostPools.map((hostPool) => (
                <option key={hostPool} value={hostPool}>
                  {hostPool}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}

      {filteredSessions.length === 0 ? (
        <div className="panel px-6 py-14 text-center">
          <h3 className="text-xl font-semibold text-ink">Keine aktiven Sitzungen</h3>
        </div>
      ) : (
        <>
          <div className="hidden overflow-hidden rounded-md border border-line bg-white lg:block">
            <table className="w-full text-left text-sm">
              <thead className="bg-soft text-xs uppercase tracking-wide text-muted">
                <tr>
                  {isAdminUser && <th className="px-4 py-3 font-semibold">Benutzer</th>}
                  <th className="px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3 font-semibold">Host Pool</th>
                  <th className="px-4 py-3 font-semibold">Session Host</th>
                  <th className="px-4 py-3 font-semibold">Erstellt</th>
                  <th className="px-4 py-3 text-right font-semibold">Aktion</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {filteredSessions.map((session) => (
                  <tr key={session.id} className="hover:bg-soft">
                    {isAdminUser && <td className="px-4 py-3 text-ink">{session.userPrincipalName}</td>}
                    <td className="px-4 py-3">
                      <span className="status-pill">{session.sessionState}</span>
                    </td>
                    <td className="px-4 py-3 text-ink">{session.hostPoolName}</td>
                    <td className="px-4 py-3 text-muted">{session.sessionHostName}</td>
                    <td className="px-4 py-3 text-muted">{formatDate(session.createTime)}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        className="btn-danger"
                        onClick={() => setSessionToTerminate(session)}
                        disabled={terminatingSession === session.id}
                      >
                        Beenden
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="grid gap-4 lg:hidden">
            {filteredSessions.map((session) => (
              <SessionCard
                key={session.id}
                session={session}
                onTerminate={setSessionToTerminate}
                isTerminating={terminatingSession === session.id}
                showUser={isAdminUser}
              />
            ))}
          </div>
        </>
      )}

      <ConfirmDialog
        isOpen={!!sessionToTerminate}
        title="Sitzung beenden"
        message={`${sessionToTerminate?.hostPoolName || ''} wird sofort getrennt.`}
        confirmLabel="Beenden"
        cancelLabel="Abbrechen"
        onConfirm={handleConfirmTerminate}
        onCancel={() => setSessionToTerminate(null)}
        isLoading={!!terminatingSession}
      />
    </div>
  );
}
